import React from 'react'
import { Link } from 'navigation'
import PropTypes from 'prop-types'
import { Button } from 'components/UIElements'
import { LINKS, SUBROUTES, SUBJECT_ID_PLACEHOLDER, FALLBACK_STRINGS, SESSION_STORAGE_KEYS } from 'utils/constants'
import D3Logo from 'assets/DCH-Logo.svg'
import { isabela, isabellaS, moreIcon, editIcon } from 'assets/assets'
import DrawerMenu from './DrawerMenu'
import {
  estimateRequiredNavWidth,
} from './navLayoutUtils'
import { logSentryEvent, SENTRY_MESSAGES } from 'utils/sentry'
import './NavBar.scss'

const COMPACT_HYSTERESIS_PX = 80

const inboxLink = {
  to: '/inbox',
  stringKey: 'navbar_inbox',
  key: 'announcements'
}

class MainNavBar extends React.PureComponent {
  constructor(props) {
    super(props)
    this.userScope = new Set(props.user.scope)
    this.state = {
      compactNav: typeof window !== 'undefined'
        && window.matchMedia('(max-width: 640px)').matches,
    }
    this.navbarRef = null
    this.linksRef = null
    this.layoutRaf = null
    this.unmounted = false
  }

  componentDidMount() {
    this.mobileMediaQuery = window.matchMedia('(max-width: 640px)')
    this.onMobileMediaQueryChange = () => {
      this.scheduleNavLayoutCheck()
    }
    this.mobileMediaQuery.addEventListener('change', this.onMobileMediaQueryChange)
    this.onWindowResize = () => {
      this.scheduleNavLayoutCheck()
    }
    window.addEventListener('resize', this.onWindowResize)

    document.body.classList.toggle('nav-compact', this.state.compactNav)
    this.scheduleNavLayoutCheck()

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!this.unmounted) {
          this.scheduleNavLayoutCheck()
        }
      })
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.strings !== this.props.strings
      || prevProps.subroute !== this.props.subroute
      || prevProps.showNavLinks !== this.props.showNavLinks
      || prevProps.caregiver !== this.props.caregiver
      || prevProps.user !== this.props.user
    ) {
      this.scheduleNavLayoutCheck()
    }
  }

  componentWillUnmount() {
    this.unmounted = true
    if (this.layoutRaf) {
      cancelAnimationFrame(this.layoutRaf)
    }
    if (this.mobileMediaQuery && this.onMobileMediaQueryChange) {
      this.mobileMediaQuery.removeEventListener('change', this.onMobileMediaQueryChange)
    }
    if (this.onWindowResize) {
      window.removeEventListener('resize', this.onWindowResize)
    }
    document.body.classList.remove('nav-compact')
  }

  setCompactNav = (compactNav) => {
    if (compactNav === this.state.compactNav) return
    this.setState({ compactNav }, () => {
      document.body.classList.toggle('nav-compact', compactNav)
    })
  }

  scheduleNavLayoutCheck = () => {
    if (this.layoutRaf) {
      cancelAnimationFrame(this.layoutRaf)
    }
    this.layoutRaf = requestAnimationFrame(() => {
      this.layoutRaf = null
      this.checkNavLayout()
    })
  }

  shouldUseCompactNav = ({ navbar, links, compactNav }) => {
    if (window.matchMedia('(max-width: 640px)').matches) {
      return true
    }

    const main = navbar.querySelector('.main')
    const containerWidth = main?.clientWidth ?? navbar.clientWidth
    const requiredWidth = estimateRequiredNavWidth(navbar, links)

    if (compactNav) {
      return requiredWidth > containerWidth - COMPACT_HYSTERESIS_PX
    }

    return requiredWidth > containerWidth
  }

  checkNavLayout = () => {
    if (this.unmounted) return

    const navbar = this.navbarRef
    const links = this.linksRef
    const { subroute, showNavLinks } = this.props
    const { compactNav } = this.state

    if (!navbar || !showNavLinks || subroute === SUBROUTES.announcements) {
      if (compactNav) {
        this.setCompactNav(false)
      }
      return
    }

    const shouldCompact = this.shouldUseCompactNav({ navbar, links, compactNav })
    this.setCompactNav(shouldCompact)
  }

  formatSelectedPtpLabel = ({ strings, selectedPtp }) => {
    const subjectId = selectedPtp?.subject_id || selectedPtp?.id
    const base = (strings.select_ptp || FALLBACK_STRINGS.select_ptp)
    if (!subjectId) return base
    // If localization explicitly includes a subject placeholder, render one-line replacement
    const tokens = [
      SUBJECT_ID_PLACEHOLDER,
      '{SubjectId}',
      '{{SubjectId}}',
      '%(SubjectId)s',
      '%SubjectId%'
    ]
    for (let i = 0; i < tokens.length; i += 1) {
      const token = tokens[i]
      if (base.includes(token)) return base.replace(token, subjectId)
    }
    const selectedPrefix = strings.selected_ptp_prefix || FALLBACK_STRINGS.selected_ptp_prefix || base
    return `${selectedPrefix}: ${subjectId}`
  }

  renderNavItem = link => {
    const {
      strings,
      subroute,
      unreadCount,
      closeDrawer,
      humiIsabellaDisabled,
      pendingConsents
    } = this.props
    const underlined = subroute === link.key
    const isInbox = subroute === SUBROUTES.announcements
    const showIsabella =
      link.key === SUBROUTES.announcements && !humiIsabellaDisabled
    const isSelectPtp = link.key === 'select_ptp'
    const linkLabel =
      (link.stringKey && !link.titleOverride)
        ? (strings?.[link.stringKey] || link.title || link.key)
        : (link.title || link.titleOverride || link.key)

    const content = (
      <span
        className={
          showIsabella ? `inbox${underlined ? ' underlined' : ''}` : ''
        }>
        {linkLabel}
        {
          strings?.[link.stringKey] === 'Profile' && pendingConsents >= 1
          && (<div className='badge'><div className='badge-red'>!</div></div>)
        }
        {isSelectPtp && (
          <span className='edit-icon-wrapper'>
            {editIcon()}
          </span>
        )}
      </span>
    )

    // keep structure consistent for disabled state to avoid layout shifts
    const commonClass = `navbar-link clickable${underlined && !showIsabella ? ' underlined' : ''}`
    const className = `${commonClass}${isSelectPtp ? ' navbar-link--select-ptp' : ''}${isInbox ? ' hidden' : ''}${link.disabled ? ' disabled' : ''}`

    return (
      <Link
        to={link.to}
        key={link.key}
        onClick={e => {
          if (link.disabled) {
            e.preventDefault()
            return
          }
          closeDrawer()
        }}
        className={className}
        id={`navbar-link-${link.key}`}>
        {content}
        {showIsabella && (
          <div className='isabella-inbox-icon'>
            <img src={isabellaS} alt='isabella-inbox-icon' />
            {unreadCount !== 0 && (
              <div className='unread-count'>
                <span>{unreadCount}</span>
              </div>
            )}
          </div>
        )}
      </Link>
    )
  }

  renderLinks = () => {
    const { showNavLinks, user, strings, caregiver } = this.props
    if (showNavLinks) {
      let computedLinks = LINKS
      if (!user?.is_caregiver) {
        computedLinks = LINKS.filter(l => l.key !== 'select_ptp')
      }
      // Check participants from Redux state first, fallback to sessionStorage
      const reduxParticipants = caregiver?.participants || []
      let participantsFromStorage = []
      try {
        const stored = sessionStorage.getItem(SESSION_STORAGE_KEYS.caregiverParticipants)
        if (stored) {
          participantsFromStorage = JSON.parse(stored) || []
        }
      } catch (_) { }
      // Use Redux participants if available, otherwise use sessionStorage
      const participants = reduxParticipants.length > 0 ? reduxParticipants : participantsFromStorage
      const isSingleAssigned = participants.length === 1
      const noAssignment = participants.length === 0
      const selectedPtp = (caregiver?.selected) || (isSingleAssigned ? participants?.[0] : null)
      const label = selectedPtp
        ? this.formatSelectedPtpLabel({ strings, selectedPtp })
        : (strings.select_ptp || FALLBACK_STRINGS.select_ptp)

      computedLinks = computedLinks.map(l => {
        if (l.key === 'select_ptp') {
          return {
            ...l,
            title: label,
            titleOverride: label,
            // If only one participant, disable the tab since it's auto-selected;
            // otherwise this tab must remain enabled.
            disabled: isSingleAssigned
          }
        }
        // Disable other tabs if caregiver must select or has no assignment
        // Only apply this logic for caregivers, not for regular participants
        return (user?.is_caregiver && noAssignment) ? { ...l, disabled: true } : l
      })

      const links = computedLinks.map(link => this.renderNavItem(link))
      return links
    }
  }

  renderSignoutButton = () => {
    const { onLogout, closeDrawer, strings } = this.props
    return (
      <Button
        className='sign-out'
        link
        onClick={() => {
          logSentryEvent({
            message: SENTRY_MESSAGES.uiSignOut,
            tags: { flow: 'auth', action: 'ui_sign_out' },
          })
          closeDrawer()
          onLogout()
        }}
        content={strings.sign_out}
      />
    )
  }

  openMenu = () => {
    const { openDrawer } = this.props
    openDrawer({
      closeOnBackgroundClick: true,
      content: (
        <DrawerMenu
          renderLinks={this.renderLinks}
          renderSignOut={this.renderSignoutButton}
        />
      )
    })
  }

  render() {
    const { user, subroute, strings, humiIsabellaDisabled } = this.props
    if (!user.token) return null
    const isInbox = subroute === SUBROUTES.announcements
    const navbarClassName = `navbar${this.state.compactNav ? ' navbar--compact' : ''}`

    return (
      <div
        className={navbarClassName}
        ref={(node) => {
          this.navbarRef = node
        }}>
        <div className='main'>
          <div className='navbar-left'>
            <Link to='/' className='logo-link'>
              <img className='d3-logo' src={D3Logo} alt='d3-logo' />
            </Link>
            {isInbox && !humiIsabellaDisabled ? (
              <img className='isabela' src={isabela} alt='isabela' />
            ) : (
              <div className='spacer-link' />
            )}
            {isInbox && (
              <span className='inbox-text'>{strings.navbar_inbox}</span>
            )}
            {isInbox && (
              <Link className='fas fa-chevron-left' to='/my-activities' />
            )}
            <div
              className={`links${isInbox ? ' none' : ''}`}
              ref={(node) => {
                this.linksRef = node
              }}>
              {this.renderLinks()}
            </div>
          </div>
          <div className='navbar-right'>
            {this.renderNavItem(inboxLink)}
            {this.renderSignoutButton()}
            {!isInbox && (
              <Button
                className='hamburger-menu'
                link
                onClick={() => this.openMenu()}
                content={moreIcon()}
              />
            )}
          </div>
        </div>
      </div>
    )
  }
}

MainNavBar.propTypes = {
  user: PropTypes.object,
  openDrawer: PropTypes.func,
  closeDrawer: PropTypes.func,
  onLogout: PropTypes.func,
  subroute: PropTypes.string,
  showNavLinks: PropTypes.bool,
  unreadCount: PropTypes.number,
  strings: PropTypes.objectOf(PropTypes.string)
}

export default MainNavBar
